import React from 'react';
import { Skeleton } from 'antd';
import { useLocation } from 'react-router-dom';
import { AccessVerificationSkeleton } from '../../access-verification/components/AccessVerificationSkeleton';
import './AgentSettingsPage.css';
import './AgentSettingsSkeleton.css';

/**
 * Loading state for the agent Settings hub. Mirrors the real layout —
 * breadcrumb, Segmented tab bar and the active tab's panel — so the page
 * doesn't jump when content arrives.
 */
export const AgentSettingsSkeleton: React.FC = () => {
  // The sidebar and Overview cards deep-link with { tab } in router state.
  const location = useLocation();
  const tab = (location.state as { tab?: string } | null)?.tab ?? 'whitelist';

  return (
  <div className="agent-settings-wrapper">
    <div className="agent-settings__breadcrumb agent-settings-skeleton__breadcrumb">
      <Skeleton.Input active size="small" style={{ width: 48, height: 14 }} />
      <span className="agent-settings-skeleton__sep">/</span>
      <Skeleton.Input active size="small" style={{ width: 96, height: 14 }} />
      <span className="agent-settings-skeleton__sep">/</span>
      <Skeleton.Input active size="small" style={{ width: 64, height: 14 }} />
    </div>

    <div className="agent-settings__body">
      <div className="agent-settings__content-card">
        {/* Segmented tab bar */}
        <div className="agent-settings-skeleton__tabs">
          <Skeleton.Button active style={{ width: 96, height: 32, borderRadius: 6 }} />
          <Skeleton.Button active style={{ width: 80, height: 32, borderRadius: 6 }} />
        </div>

        {/* Active panel. Defaults to Whitelist — the tab the hub opens on —
            unless a deep-link preselected Billing via router state. */}
        <div className="agent-settings-skeleton__panel">
          {tab === 'billing' ? <BillingCardSkeleton /> : <AccessVerificationSkeleton embedded />}
        </div>
      </div>
    </div>
  </div>
  );
};

/** The billing plan card: heading, lead, one full-width field and two rows of
 *  paired fields, matching BillingSection's grid. */
function BillingCardSkeleton() {
  return (
    <div className="billing-card agent-settings-skeleton__card">
      <div>
        <Skeleton.Input active style={{ width: 190, height: 20, marginBottom: 8 }} />
        <Skeleton.Input active size="small" style={{ width: '70%', height: 14 }} />
      </div>

      <FieldSkeleton labelWidth={124} />

      <div className="agent-settings-skeleton__row">
        <FieldSkeleton labelWidth={68} />
        <FieldSkeleton labelWidth={148} />
      </div>

      <div className="agent-settings-skeleton__row">
        <FieldSkeleton labelWidth={64} />
        <FieldSkeleton labelWidth={56} />
      </div>

      <FieldSkeleton labelWidth={100} />

      <div className="agent-settings-skeleton__actions">
        <Skeleton.Button active style={{ width: 124, height: 32, borderRadius: 8 }} />
      </div>
    </div>
  );
}

/** Label + 44px control, the metric every billing field uses. */
function FieldSkeleton({ labelWidth }: { labelWidth: number }) {
  return (
    <div className="agent-settings-skeleton__field">
      <Skeleton.Input active size="small" style={{ width: labelWidth, height: 14 }} />
      <Skeleton.Input active block style={{ width: '100%', height: 44, borderRadius: 12 }} />
    </div>
  );
}
